<script setup lang="ts">
import { api } from "@/api";
import { computed, ref, watch } from "vue";
import NetworkIcon from "~icons/mdi/network-outline";
import type { NetworkParam } from "../../common/types.common";
import { defineActions, defineState } from "../common";
import { FormInputCheckbox, FormInputSelect, FormInputText } from "../input";
import { FormMessage, FormPanel, FormTool } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type ModelType = NetworkParam;
interface PropsType extends BaseProps<ModelType> {
    toggleable?: boolean;
    placeholder?: {
        [K in keyof ModelType]: string;
    };
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    toggleable: false,
    value: undefined,
});

const model = defineModel<ModelType>({
    required: true,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => {
        if (model.value.isActive) {
            return model.value.ipGroup !== undefined && model.value.ipAddress !== undefined;
        } else {
            return true;
        }
    },
});

const actions = defineActions(model, props, state);

const prevAddress = ref(model.value.ipAddress);
const collapsed = computed(() => {
    if (props.toggleable) {
        return !model.value.isActive;
    } else {
        return false;
    }
});

watch(
    () => state.editMode,
    () => (prevAddress.value = model.value.ipAddress),
);

// watch(
//     () => props.toggleable,
//     () => (model.value.isActive = props.toggleable === false),
//     { immediate: true },
// );
</script>

<template>
    <FormPanel :collapsed="collapsed" :param="props">
        <template #header>
            <span class="inline-flex flex-row items-center gap-3">
                <FormInputCheckbox
                    v-if="props.toggleable"
                    v-model="model.isActive"
                    class="ml-1"
                    :disabled="!state.editMode"
                    embedMode="inline"
                />
                <NetworkIcon v-else class="ml-1" />
                <span class="font-semibold text-gray-600">
                    {{ props.label ?? "Pengaturan Jaringan" }}
                </span>
            </span>
        </template>

        <template #icons>
            <span class="inline-flex flex-row">
                <FormTool :actions="actions" :param="props" :showUpdate="true" :state="state" />
            </span>
        </template>

        <div v-if="state.hasError" class="p-4">
            <FormMessage :state="state" />
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormInputSelect
                v-model="model.ipGroup"
                embedMode="embed"
                label="Jaringan"
                :mode="state.nestMode"
                :options="api.networkGroup.formHooks.select"
                searchable
            />
            <FormInputSelect
                v-if="state.editMode"
                v-model="model.ipAddress"
                embedMode="embed"
                label="Alamat IP"
                :mode="state.nestMode"
                :options="api.networkUsage.formHooks.select(model.ipGroup, prevAddress)"
                searchable
            />
            <FormInputText
                v-else
                v-model="model.ipAddress"
                embedMode="embed"
                label="Alamat IP"
                :mode="state.nestMode"
            />
        </div>
    </FormPanel>
</template>
